import React, {
  Key,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, navigate, PageProps } from 'gatsby'
import clsx from 'clsx'
import moment from 'moment'
import Layout from 'components/layouts/tow-column'
import {
  Alert,
  Button,
  Card,
  Divider,
  Error,
  ErrorAlert,
  Form,
  Input,
  message,
  SEO,
  showMessage,
  Spin,
  Text,
  Title,
} from 'components/common'
import {
  DiscountFeeInput,
  ExtensionFeeInput,
  OrderPurpose,
  OrderUpdateInput,
  OrderVariantInput,
  useCustomerUserMetaDataUpdateMutation,
  useGeneralSettingQuery,
  useOrderQuery,
  useOrderUpdateMutation,
  usePriceCalculateMutation,
  useProductsQuery,
  useRatesQuery,
} from 'lib/graphql/storeadmin'
import { bookingsErrorHandling } from 'lib/hooks'
import {
  BookingDatetimeSelect,
  BookingDetail,
  BookingInfoCard,
  BookingsUserMetaData,
  BookingUsageScheduleDetail,
  ExtensionFeeType,
  PriceDisplayCard,
  PriceList,
  SelectedVariant,
  TimezoneSelectorModalCascader,
  VariantSelectorModalForm,
} from 'components/bookings'
import { Anchor } from '../status'
import { price } from 'lib/str-formatter'
import { routes } from 'config'
import * as styles from './styles.module.css'
import ProductSelectCard from 'components/bookings/cards/ProductSelectCard'
import SagaArenaProductSelectCard from 'components/bookings/cards/SagaArenaProductSelectCard'

const onBack = () => navigate('../')

type AlertMessage = {
  datetime?: string
  products?: string
  reason?: string
}

export type EditFormType = {
  modifiedReason?: string
}

export const Head = () => <SEO title="予約の編集" />

const EditPage = ({ params: { id } }: PageProps): JSX.Element => {
  const today = moment(Date.now()).format('YYYY-MM-DD')
  const [form] = Form.useForm<EditFormType>()
  const [variantForm] = Form.useForm<VariantSelectorModalForm>()
  const { data, loading, error } = useOrderQuery({
    variables: { id: id },
  })
  const { loading: productsLoading } = useProductsQuery()
  const { data: settingsData } = useGeneralSettingQuery()
  const [updateUserMetaData] = useCustomerUserMetaDataUpdateMutation()
  const [getPrices, { loading: priceLoading }] = usePriceCalculateMutation({})
  const {
    data: ratesData,
    loading: ratesLoading,
    error: ratesError,
  } = useRatesQuery({})
  const [OrderUpdate, { loading: updateLoading, error: updateError }] =
    useOrderUpdateMutation()

  const [hasCustomerId, setHasCustomerId] = useState(false)
  const [bookingDates, setBookingDates] =
    useState<[moment.Moment, moment.Moment]>(null)
  const [timezone, setTimezone] = useState<[moment.Moment, moment.Moment]>()
  const [initBookingDates, setInitBookingDates] =
    useState<[moment.Moment, moment.Moment]>(null)
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([])
  const alreadyPay = useRef<number>(0)
  const [alert, setAlert] = useState<AlertMessage>({})
  const [bookingDetail, setBookingDetail] = useState<BookingDetail>()
  const [bookingUsageScheduleDetail, setBookingUsageScheduleDetail] =
    useState<BookingUsageScheduleDetail>({})
  const [userMetaData, setUserMetaData] = useState<BookingsUserMetaData>({
    limit: 0,
    discount: 0,
  })
  const [priceList, setPriceList] = useState<PriceList>({
    originalSubTotalPrice: 0,
    originalSubTotalPriceTaxExcluded: 0,
    originalTotalTax: 0,
    originalTotalPrice: 0,
    subTotal: 0,
    subTotalPriceTaxExcluded: 0,
    totalTax: 0,
    totalPrice: 0,
    discountPrice: 0,
    specialPriceIncluded: false,
  })
  const [discountData, setDiscountData] = useState<DiscountFeeInput>()
  const [extensionData, setExtensionData] = useState<ExtensionFeeType[]>([])
  const [rateVariantId, setRateVariantId] = useState<string>(null)
  const [rateDefaultTitle, setRateDefaultTitle] = useState<string>(null)
  const [checkManualPeriodTime, setManualPeriodTime] = useState<boolean>(false)
  const [selectedVariants, setSelectedVariants] =
    useState<SelectedVariant[]>(null)
  const [sagaArenaSelectedVariants, setSagaArenaSelectedVariants] = useState<
    OrderVariantInput[]
  >([])
  const [sagaArenaPurpose, setSagaArenaPurpose] = useState<OrderPurpose>(null)
  const [sagaArenaEstimateNumber, setSagaArenaEstimateNumber] = useState({
    first: '',
    second: '',
  })
  // SAGAアリーナかどうかをチェック
  const isSagaArena = useMemo(() => {
    return settingsData?.generalSetting.domain === 'saga-arena'
  }, [settingsData])

  const rateId = useRef<string>(null)

  // Bookings error useState
  const [showBookingsErrorAlert, setShowBookingsErrorAlert] = useState(false)
  const [bookingsErrorDescriptions, setBookingsErrorDescriptions] = useState<
    string[]
  >([])

  // 初期値をセットする
  useEffect(() => {
    if (data?.order) {
      const {
        bookingDates,
        contactOfRepresentative,
        customer,
        discountFee,
        period,
        payment,
        lineItems,
        estimateNumber,
        extensionFee,
        entryFee,
        saleOfGoods,
        purpose,
        numberOfPeople,
        calendar,
        rate,
        originalSubTotalPrice,
        originalSubTotalPriceTaxExcluded,
        originalTotalTax,
        originalTotalPrice,
        subTotalPrice,
        subTotalPriceTaxExcluded,
        totalTax,
        totalPrice,
        specialPriceIncluded,
        discountPrice,
      } = data?.order || {}

      if (isSagaArena) {
        setSagaArenaPurpose(purpose)
        // 見積番号を枝番の前後で分ける
        if (estimateNumber?.length > 10) {
          const [first, second] = estimateNumber.split('-')
          setSagaArenaEstimateNumber({
            first,
            second,
          })
        }
      }

      const prices: PriceList = {
        originalSubTotalPrice: originalSubTotalPrice,
        originalSubTotalPriceTaxExcluded: originalSubTotalPriceTaxExcluded,
        originalTotalTax: originalTotalTax,
        originalTotalPrice: originalTotalPrice,
        subTotal: subTotalPrice,
        subTotalPriceTaxExcluded: subTotalPriceTaxExcluded,
        totalTax: totalTax,
        totalPrice: totalPrice,
        specialPriceIncluded: specialPriceIncluded,
        discountPrice: discountPrice,
      }
      prices.notPay =
        totalPrice - alreadyPay.current > 0
          ? totalPrice - alreadyPay.current
          : 0
      setPriceList(prices)
      setHasCustomerId(!!customer.id)
      setBookingDates([
        moment(bookingDates[0].date),
        moment(bookingDates[bookingDates?.length - 1].date),
      ])
      setInitBookingDates([
        moment(bookingDates[0].date),
        moment(bookingDates[bookingDates?.length - 1].date),
      ])
      setTimezone([
        moment(`${today} ${period?.start}`),
        moment(`${today} ${period?.end}`),
      ])
      setCheckedKeys(lineItems?.edges?.map(({ lineItem }) => lineItem?.variant))
      alreadyPay.current = payment?.amount
      setBookingDetail({
        numberOfPeople: numberOfPeople,
        purpose: purpose,
        exceed: extensionFee?.reduce((sum, item) => sum + item.value, 0),
        entryFee: entryFee,
        saleOfGoods: saleOfGoods,
        contactOfRepresentative,
      })
      setBookingUsageScheduleDetail({
        approval: calendar?.approval,
        organizer: calendar?.organizer,
        eventName: calendar?.eventName,
        publishableDate: calendar?.publishableDate,
      })
      setUserMetaData({
        limit: customer?.userMetaData?.limit,
        discount: customer?.userMetaData?.discount,
      })
      setDiscountData({
        value: discountFee?.value,
        reason: discountFee?.reason,
      })
      // extensionFeeの型を合わせる
      const extensionFeeAddId = extensionFee
        ? extensionFee.map((item) => ({
            id: null,
            reason: item.reason,
            value: item.value,
          }))
        : []
      setExtensionData(extensionFeeAddId)
      setRateVariantId(rate?.variantId || null)
      setRateDefaultTitle(rate?.title.jaJp)
      if (rate === null && period?.start && period?.end) {
        setManualPeriodTime(true)
      }
    }
  }, [data, isSagaArena, today])

  /*
    日付と時刻が選択されている ⇒ 施設選択活性
    日付と時刻が選択されていない ⇒ 施設選択非活性
    施設が選択されている ⇒ 日時選択非活性
    施設が選択されていない ⇒ 日時選択活性
  */
  const selectButton = useMemo<boolean>(() => {
    return !(bookingDates && timezone)
  }, [bookingDates, timezone])

  const inactive = useMemo<boolean>(() => {
    return selectedVariants?.length > 0 || sagaArenaSelectedVariants?.length > 0
  }, [selectedVariants, sagaArenaSelectedVariants])

  const dataSource = useMemo<Array<TimezoneSelectorModalCascader>>(() => {
    return ratesData?.rates?.edges?.map(({ rate }) => {
      const { id, title, variants } = rate
      return {
        key: id,
        title: title.jaJp,
        variants: variants?.edges?.map(({ variant }) => ({
          start: variant.period.start,
          end: variant.period.end,
          id: id,
          category: title.jaJp,
          variantId: variant.id,
        })),
      }
    })
  }, [ratesData])

  const handleDiffDates = (): Array<string> => {
    if (!bookingDates) return []
    const startDate = bookingDates[0]?.clone()
    const dates: string[] = [startDate.format('YYYY-MM-DD')]
    while (bookingDates[1].diff(startDate, 'days') > 0) {
      startDate.add(1, 'days')
      dates.push(startDate.format('YYYY-MM-DD'))
    }
    return dates
  }

  const onCheck = (keys: Key[]) => {
    setCheckedKeys(keys)
  }

  const setExceedValue = (values: ExtensionFeeType[]) => {
    if (values) {
      setBookingDetail({
        ...bookingDetail,
        exceed: Number(values.reduce((sum, item) => sum + item.value, 0)),
      })
      setExtensionData(values)
    }
  }

  const setDiscountValue = (values: DiscountFeeInput) => {
    if (values) {
      setBookingDetail({ ...bookingDetail, discount: values?.value })
      setDiscountData(values)
    }
  }

  const handleAlert = (): AlertMessage => {
    const messages: AlertMessage = {}
    if (!bookingDates || !timezone)
      messages.datetime = '利用予定日と時間帯の入力は必須です'

    if (!form.getFieldValue('modifiedReason'))
      messages.reason = '編集理由の入力は必須です'
    if (isSagaArena) {
      if (!sagaArenaSelectedVariants?.length)
        messages.products = '予約する対象を選択してください'
    } else {
      if (!selectedVariants?.length)
        messages.products = '予約する対象を選択してください'
    }
    return messages
  }

  const onCloseAlert = () => {
    setShowBookingsErrorAlert(false)
  }

  const clearProductData = () => {
    setCheckedKeys([])
    variantForm.resetFields()
    setManualPeriodTime(false)
    if (isSagaArena) {
      setSagaArenaSelectedVariants([])
    }
  }

  /**
   * 予約情報を更新する
   */
  const onUpdate = async (): Promise<void> => {
    const messages = handleAlert()
    setAlert(messages)

    if (Object.keys(messages).length) {
      return message.error('入力値が不足しています。')
    }

    // 予約更新用にバリエーションリストのひな型を定義
    let inputVariants: OrderVariantInput[] = []

    // SAGAアリーナとそれ以外のストアでバリエーションのデータ構造が異なるため、
    // 処理を分岐してinputVariantsにデータを格納する
    if (isSagaArena) {
      inputVariants = sagaArenaSelectedVariants?.map((variant) => {
        return {
          variantId: variant.variantId,
          arenaBookingInfo: variant.arenaBookingInfo,
        }
      })
    } else {
      inputVariants = selectedVariants?.map((variant) => {
        return {
          variantId: variant.variantId,
          quantity: 1,
        }
      })
    }

    try {
      const extensionFeeData: ExtensionFeeInput[] = extensionData.map(
        ({ id, ...inputObject }) => inputObject
      )
      const discountFeeData =
        discountData?.reason !== '' &&
        typeof discountData?.reason !== 'undefined'
          ? discountData
          : null

      // 見積番号
      const { first, second } = sagaArenaEstimateNumber

      // 予約更新用オブジェクトを作成する
      const input: OrderUpdateInput = {
        bookingDate: {
          start: bookingDates[0].format('YYYY-MM-DD'),
          end: bookingDates[1].format('YYYY-MM-DD'),
        },
        period:
          checkManualPeriodTime && rateVariantId === null
            ? {
                start: timezone[0].format('HH:mm'),
                end: timezone[1].format('HH:mm'),
              }
            : null,
        productVariants: inputVariants,
        estimateNumber:
          isSagaArena && first?.length > 0 && second?.length > 0
            ? first + '-' + second
            : null,
        contactOfRepresentative: {
          name: bookingDetail.contactOfRepresentative?.name,
          email: bookingDetail.contactOfRepresentative?.email,
          phone: bookingDetail.contactOfRepresentative?.phone,
        },
        purpose: isSagaArena ? sagaArenaPurpose : bookingDetail.purpose,
        saleOfGoods: bookingDetail.saleOfGoods,
        entryFee: bookingDetail.entryFee,
        rateVariantId:
          !checkManualPeriodTime && rateVariantId !== null
            ? rateVariantId
            : null,
        numberOfPeople: bookingDetail.numberOfPeople,
        payment: {
          amount: alreadyPay?.current,
          totalRefunded: data?.order?.refund?.totalRefunded,
        },
        extensionFee: extensionFeeData,
        discountFee: discountFeeData,
        calendar: {
          approval: bookingUsageScheduleDetail?.approval,
          organizer: bookingUsageScheduleDetail?.organizer,
          eventName: bookingUsageScheduleDetail?.eventName,
          publishableDate: bookingUsageScheduleDetail?.publishableDate,
        },
        customerId: data.order.customer.id,
        modifiedReason: form.getFieldValue('modifiedReason'),
      }

      if (userMetaData !== null) {
        try {
          await updateUserMetaData({
            variables: {
              id: data?.order?.customer?.id,
              input: {
                limit: Number(userMetaData?.limit),
                discount: Number(userMetaData?.discount),
              },
            },
          })
        } catch (e) {
          console.error(e)
          showMessage(
            'error',
            <>
              <div>予約編集時にエラーが発生しました</div>
              <div>{e.message}</div>
            </>
          )
        }
      }

      const { data: result } = await OrderUpdate({
        variables: { id: id, input: input },
      })
      const orderEditErrors = result?.orderUpdate?.orderUserErrors
      if (orderEditErrors.length > 0) {
        const orderEditErrorsDescriptions =
          bookingsErrorHandling(orderEditErrors)
        if (
          orderEditErrorsDescriptions[0] ===
          'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、予約を編集しました。'
          )
          await navigate('../')
        } else {
          setShowBookingsErrorAlert(true)
          showMessage('error', '予約編集時にエラーが発生しました')
          setBookingsErrorDescriptions(orderEditErrorsDescriptions)
        }
      } else if (updateError) {
        throw updateError
      } else {
        setShowBookingsErrorAlert(false)
        showMessage('success', '予約を編集しました。')
        await navigate(`${routes.bookings}/${result?.orderUpdate.order.id}`)
      }
    } catch (e) {
      console.error(e)
      showMessage(
        'error',
        <>
          <div>予約編集時にエラーが発生しました</div>
          <div>{e.message}</div>
        </>
      )
    }
  }

  /**
   * 料金を計算して、料金リストのstateを更新する
   */
  const calculatePrices = useCallback(
    async (inputVariants: OrderVariantInput[] = []) => {
      if (!Array.isArray(inputVariants)) {
        console.error('inputVariantsは配列ではありません', inputVariants)
        return
      }
      const isVariant = inputVariants.every(({ variantId }) => variantId)
      if (!isVariant) return
      try {
        const extensionFeeData: ExtensionFeeInput[] = extensionData.map(
          ({ id, ...inputObject }) => inputObject
        )

        const discountFeeData =
          discountData?.reason !== '' &&
          typeof discountData?.reason !== 'undefined'
            ? discountData
            : null

        const response = await getPrices({
          variables: {
            input: {
              customerId: data.order.customer.id,
              productVariants: inputVariants,
              period: {
                start: timezone[0].format('HH:mm'),
                end: timezone[1].format('HH:mm'),
              },
              bookingDates: {
                start: bookingDates[0].format('YYYY-MM-DD'),
                end: bookingDates[bookingDates?.length - 1].format(
                  'YYYY-MM-DD'
                ),
              },
              exceed: extensionFeeData,
              discountFee: discountFeeData?.value,
              orderId: data.order.id,
            },
          },
        })

        const {
          originalSubTotalPrice,
          originalSubTotalPriceTaxExcluded,
          originalTotalTax,
          originalTotalPrice,
          subTotalPrice,
          subTotalPriceTaxExcluded,
          totalTax,
          totalPrice,
          specialPriceIncluded,
          discountPrice,
        } = response.data?.priceCalculate?.price || {}

        const prices: PriceList = {
          originalSubTotalPrice: originalSubTotalPrice,
          originalSubTotalPriceTaxExcluded: originalSubTotalPriceTaxExcluded,
          originalTotalTax: originalTotalTax,
          originalTotalPrice: originalTotalPrice,
          subTotal: subTotalPrice,
          subTotalPriceTaxExcluded: subTotalPriceTaxExcluded,
          totalTax: totalTax,
          totalPrice: totalPrice,
          specialPriceIncluded: specialPriceIncluded,
          discountPrice: discountPrice,
        }
        prices.notPay =
          totalPrice - alreadyPay.current > 0
            ? totalPrice - alreadyPay.current
            : 0
        setPriceList(prices)
      } catch (e) {
        console.log(e)
      }
    },
    [
      bookingDates,
      data?.order?.customer?.id,
      discountData,
      extensionData,
      getPrices,
      timezone,
    ]
  )

  // 料金計算処理の実行
  useEffect(() => {
    if (priceLoading || !hasCustomerId || productsLoading) return
    if (
      hasCustomerId &&
      bookingDates &&
      timezone &&
      (selectedVariants?.length > 0 || sagaArenaSelectedVariants?.length > 0)
    ) {
      let inputVariants: OrderVariantInput[] = []
      if (isSagaArena) {
        inputVariants = sagaArenaSelectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            arenaBookingInfo: variant.arenaBookingInfo.map((item) => ({
              dateOfUse: item.dateOfUse,
              quantity: item.quantity,
            })),
          }
        })
      } else {
        inputVariants = selectedVariants?.map((variant) => {
          return {
            variantId: variant.variantId,
            quantity: 1,
          }
        })
      }
      // 料金を計算し、料金リストのstateを更新する
      calculatePrices(inputVariants)
    } else {
      setPriceList({
        originalSubTotalPrice: 0,
        originalSubTotalPriceTaxExcluded: 0,
        originalTotalTax: 0,
        originalTotalPrice: 0,
        subTotal: 0,
        subTotalPriceTaxExcluded: 0,
        totalTax: 0,
        totalPrice: 0,
        specialPriceIncluded: false,
        discountPrice: 0,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingDates,
    bookingDetail,
    hasCustomerId,
    sagaArenaSelectedVariants,
    timezone,
  ])

  if (loading) {
    return (
      <div className="fixed left-0 top-0 w-screen h-screen flex justify-center items-center">
        <Spin tip="Loading..." size="large" />
      </div>
    )
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <Layout
        title="予約を編集する"
        actions={<Anchor />}
        loading={loading}
        onBack={onBack}
      >
        <Layout.Section size="large">
          <Layout.Item>
            {showBookingsErrorAlert && (
              <ErrorAlert
                alertDescription={bookingsErrorDescriptions}
                onCloseAlert={onCloseAlert}
              />
            )}
            <Card shadow loading={loading}>
              <div className={clsx('flex', 'justify-between', 'items-center')}>
                <div className={clsx('flex-initial')}>
                  <Title level={5}>予約日時を変更する</Title>
                </div>

                {(selectedVariants?.length ||
                  sagaArenaSelectedVariants.length > 0) && (
                  <div className={clsx('flex', 'flex-auto', 'justify-end')}>
                    <Button
                      style={{
                        marginLeft: '3px',
                      }}
                      onClick={clearProductData}
                    >
                      予約日時を変更
                    </Button>
                  </div>
                )}
              </div>

              <div className={clsx('flex', 'justify-between', 'items-start')}>
                <BookingDatetimeSelect
                  date={bookingDates}
                  timezone={timezone}
                  dataSource={dataSource}
                  onDateChange={setBookingDates}
                  onTimezoneChange={setTimezone}
                  loading={ratesLoading}
                  error={ratesError}
                  rateId={rateId}
                  rateDefaultTitle={rateDefaultTitle}
                  inactive={inactive}
                  checkManualPeriodTime={checkManualPeriodTime}
                  setManualPeriodTime={setManualPeriodTime}
                  setRateVariantId={setRateVariantId}
                />
              </div>
            </Card>
            {alert?.datetime && (
              <Alert
                className="my-2"
                showIcon
                message={alert.datetime}
                type="error"
              />
            )}
          </Layout.Item>

          <Layout.Item>
            {isSagaArena ? (
              <SagaArenaProductSelectCard
                isEdit
                bookingDates={bookingDates}
                initBookingDates={initBookingDates}
                checkedKeys={checkedKeys}
                calculatePrices={calculatePrices}
                hasCustomerId={data?.order?.customer?.id ? true : false}
                disabled={selectButton}
                form={form}
                handleDiffDates={handleDiffDates}
                isSagaArena={isSagaArena}
                lineItems={data?.order?.lineItems?.edges.map(
                  (edge) => edge.lineItem
                )}
                onCheck={onCheck}
                purpose={sagaArenaPurpose}
                priceLoading={priceLoading}
                rateId={rateId}
                setCheckedKeys={setCheckedKeys}
                setPurpose={setSagaArenaPurpose}
                selectedVariants={selectedVariants}
                setSelectedVariants={setSelectedVariants}
                setSagaArenaSelectedVariants={setSagaArenaSelectedVariants}
                sagaArenaSelectedVariants={sagaArenaSelectedVariants}
                sagaArenaEstimateNumber={sagaArenaEstimateNumber}
                setSagaArenaEstimateNumber={setSagaArenaEstimateNumber}
                specialPriceIncluded={priceList.specialPriceIncluded}
                timezone={timezone}
              />
            ) : (
              <Card shadow>
                <ProductSelectCard
                  bookingDates={bookingDates}
                  checkedKeys={checkedKeys}
                  hasCustomerId={data?.order?.customer?.id ? true : false}
                  disabled={selectButton}
                  form={form}
                  handleDiffDates={handleDiffDates}
                  onCheck={onCheck}
                  rateId={rateId}
                  selectedVariants={selectedVariants}
                  setSelectedVariants={setSelectedVariants}
                  timezone={timezone}
                  isSagaArena={isSagaArena}
                  lineItems={data?.order?.lineItems?.edges?.map(
                    (edge) => edge.lineItem
                  )}
                  calculatePrices={calculatePrices}
                />
              </Card>
            )}
            {alert?.products && (
              <Alert showIcon message={alert.products} type="error" />
            )}
          </Layout.Item>

          <Layout.Item>
            <PriceDisplayCard
              title={<Title level={5}>支払い</Title>}
              editable={true}
              extensionData={extensionData}
              setExceedValue={setExceedValue}
              discountData={discountData}
              setDiscountValue={setDiscountValue}
              priceList={priceList}
              alreadyPaid={alreadyPay.current}
              loading={priceLoading}
            />
          </Layout.Item>

          <Layout.Item>
            <Card shadow loading={loading}>
              <Form form={form} className="space-y-2">
                <Title level={5}>編集理由</Title>
                <Form.Item noStyle name="modifiedReason">
                  <Input autoComplete="off" />
                </Form.Item>
                <Text mute>あなたとスタッフだけがこの理由を閲覧できます。</Text>
                {alert?.reason && (
                  <Alert showIcon message={alert.reason} type="error" />
                )}
              </Form>
            </Card>
          </Layout.Item>
        </Layout.Section>

        <Layout.Section size="small">
          <Layout.Item>
            <Card shadow loading={priceLoading}>
              <Title level={5}>サマリー</Title>

              <div className={clsx(styles.flex__between__center)}>
                <div className={clsx('flex-initial')}>更新後の合計</div>
                <div className={clsx(styles.flex__auto__end)}>
                  {price(priceList.totalPrice)}
                </div>
              </div>
              <div className={clsx(styles.flex__between__center)}>
                <div className={clsx('flex-initial')}>お客様支払い済</div>
                <div className={clsx(styles.flex__auto__end)}>
                  {price(alreadyPay.current)}
                </div>
              </div>

              <Divider />

              <div className={clsx(styles.flex__between__center)}>
                <div className={clsx('flex-initial')}>徴収額</div>
                <div className={clsx(styles.flex__auto__end)}>
                  {price(priceList.notPay ?? 0)}
                </div>
              </div>
            </Card>
          </Layout.Item>

          <Layout.Item>
            <BookingInfoCard
              customer={{
                id: data?.order?.customer?.id,
                firstName: data?.order?.customer?.firstName,
                lastName: data?.order?.customer?.lastName,
                corporationName: data?.order?.customer?.corporationName,
                customerType: data?.order?.customer?.type,
                email: data?.order?.customer?.email,
                phone: data?.order?.customer?.phone,
              }}
              bookingDetail={bookingDetail}
              onDetailHold={setBookingDetail}
              discountDetail={{
                discount: userMetaData?.discount,
                limit: userMetaData?.limit,
              }}
              usageScheduleDetails={{
                approval: bookingUsageScheduleDetail.approval,
                organizer: bookingUsageScheduleDetail.organizer,
                eventName: bookingUsageScheduleDetail.eventName,
                publishableDate: bookingUsageScheduleDetail.publishableDate,
              }}
              onUsageScheduleDetailsHold={setBookingUsageScheduleDetail}
              onUserMetaDataHold={setUserMetaData}
              loading={loading}
              editable={true}
              isArena={isSagaArena}
            />
          </Layout.Item>
        </Layout.Section>

        <Layout.Footer
          left={
            <>
              <Link to="../">
                <Button danger>キャンセル</Button>
              </Link>
            </>
          }
          right={
            <div className={clsx('flex', 'justify-end')}>
              <Button
                type={'primary'}
                loading={updateLoading}
                onClick={onUpdate}
              >
                保存する
              </Button>
            </div>
          }
        />
      </Layout>
    </>
  )
}

export default EditPage
